export const NOTE_STYLING_CONSTANTS = {
    FONT_FAMILY: 'Roboto, Helvetica, Arial, sans-serif',
    // Reduced font sizes
    FONT_SIZE_SECTION_HEADER_PX: 14, // Slightly smaller header
    FONT_SIZE_BODY_PX: 12,           // Slightly smaller body
    FONT_SIZE_METADATA_PX: 10,       // Slightly smaller metadata
    COLOR_TEXT_HEADER_CSS: 'rgb(25, 25, 25)',
    COLOR_TEXT_BODY_CSS: 'rgb(64, 64, 64)',
    // Reduced spacing around headers
    SPACING_BEFORE_SECTION_HEADER_PX: 8, // Less space before
    SPACING_AFTER_SECTION_HEADER_PX: 4,  // Less space after
    // Reduced line height
    LINE_HEIGHT_MULTIPLIER_BODY: 1.3, // More compact lines
    // Indents remain the same relative to font size, but absolute pixel values change
    BULLET_INDENT_LEVEL_1_PX: 20,
    BULLET_INDENT_LEVEL_2_PX: 40,
    SUB_ITEM_INDENT_PX: 30,
};

const escapeHtml = (unsafe: string): string => {
    if (unsafe === null || unsafe === undefined) {
        return '';
    }
    return unsafe
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
};

export function generateNoteHtmlPreview(rawText: string): string {
    if (rawText === null || rawText === undefined || typeof rawText !== 'string') {
        // Use updated constants for default empty state
        return `<div style="font-family: ${NOTE_STYLING_CONSTANTS.FONT_FAMILY}; line-height: ${NOTE_STYLING_CONSTANTS.LINE_HEIGHT_MULTIPLIER_BODY}; font-size: ${NOTE_STYLING_CONSTANTS.FONT_SIZE_BODY_PX}px; color: ${NOTE_STYLING_CONSTANTS.COLOR_TEXT_BODY_CSS};"><div style="min-height: ${NOTE_STYLING_CONSTANTS.FONT_SIZE_BODY_PX * NOTE_STYLING_CONSTANTS.LINE_HEIGHT_MULTIPLIER_BODY}px;"><br></div></div>`;
    }
    if (rawText === "") {
         // Use updated constants for empty string state
         return `<div style="font-family: ${NOTE_STYLING_CONSTANTS.FONT_FAMILY}; line-height: ${NOTE_STYLING_CONSTANTS.LINE_HEIGHT_MULTIPLIER_BODY}; font-size: ${NOTE_STYLING_CONSTANTS.FONT_SIZE_BODY_PX}px; color: ${NOTE_STYLING_CONSTANTS.COLOR_TEXT_BODY_CSS};"><div style="min-height: ${NOTE_STYLING_CONSTANTS.FONT_SIZE_BODY_PX * NOTE_STYLING_CONSTANTS.LINE_HEIGHT_MULTIPLIER_BODY}px;"><br></div></div>`;
    }

    const lines = rawText.split('\n');
    // Use updated constants for main container
    let htmlOutput = `<div style="font-family: ${NOTE_STYLING_CONSTANTS.FONT_FAMILY}; line-height: ${NOTE_STYLING_CONSTANTS.LINE_HEIGHT_MULTIPLIER_BODY}; font-size: ${NOTE_STYLING_CONSTANTS.FONT_SIZE_BODY_PX}px; color: ${NOTE_STYLING_CONSTANTS.COLOR_TEXT_BODY_CSS};">`;
    let previousLineEndedWithColon = false;

    for (const line of lines) {
        let textToRender = escapeHtml(line);
        // Use updated constants for min-height
        let lineStyles = `margin: 0; padding: 0; word-wrap: break-word; white-space: pre-wrap; min-height: ${NOTE_STYLING_CONSTANTS.FONT_SIZE_BODY_PX * NOTE_STYLING_CONSTANTS.LINE_HEIGHT_MULTIPLIER_BODY}px;`;

        if (line === "") {
            htmlOutput += `<div style="${lineStyles}"><br></div>`;
            previousLineEndedWithColon = false;
            continue;
        }

        const trimmedLineForPatternMatching = line.trim();
        // Regex remains the same (correctly handles accents)
        const isSectionHeader = /^[A-ZÀÂÄÈÉÊËÎÏÔÖÙÜÇ\s]+:$/.test(trimmedLineForPatternMatching);
        const isNestedBullet = line.match(/^(\s{2,})-.*/);
        const isBulletLevel1 = !isNestedBullet && line.match(/^(\s{0,1})-.*/);
        const isSubItemByIndent = !isNestedBullet && !isBulletLevel1 && line.match(/^(\s{2,}).*/);

        if (isSectionHeader) {
            // Use updated constants for header styling
            lineStyles += ` font-size: ${NOTE_STYLING_CONSTANTS.FONT_SIZE_SECTION_HEADER_PX}px; color: ${NOTE_STYLING_CONSTANTS.COLOR_TEXT_HEADER_CSS}; font-weight: bold;`;
            lineStyles += ` margin-top: ${NOTE_STYLING_CONSTANTS.SPACING_BEFORE_SECTION_HEADER_PX}px; margin-bottom: ${NOTE_STYLING_CONSTANTS.SPACING_AFTER_SECTION_HEADER_PX}px;`;
            previousLineEndedWithColon = false;
        } else if (isNestedBullet) {
            // Use updated constants for indent
            lineStyles += ` padding-left: ${NOTE_STYLING_CONSTANTS.BULLET_INDENT_LEVEL_2_PX}px;`;
            previousLineEndedWithColon = false;
        } else if (isBulletLevel1) {
            // Use updated constants for indent
            lineStyles += ` padding-left: ${NOTE_STYLING_CONSTANTS.BULLET_INDENT_LEVEL_1_PX}px;`;
            if (trimmedLineForPatternMatching.endsWith(':')) {
                previousLineEndedWithColon = true;
            } else {
                previousLineEndedWithColon = false;
            }
        } else if (isSubItemByIndent || previousLineEndedWithColon) {
            // Use updated constants for indent
            lineStyles += ` padding-left: ${NOTE_STYLING_CONSTANTS.SUB_ITEM_INDENT_PX}px;`;
            previousLineEndedWithColon = false;
        } else {
            previousLineEndedWithColon = false;
        }

        // Handle lines that are just whitespace or empty after trim but had spaces
        if (line.trim() === "" && line !== "") {
             textToRender = line.replace(/ /g, ' '); // Replace spaces with non-breaking spaces to preserve indentation
        } else if (line.trim() === "") {
             textToRender = '<br>'; // Ensure empty lines render correctly
        }


        htmlOutput += `<div style="${lineStyles}">${textToRender}</div>`;
    }

    htmlOutput += `</div>`;
    return htmlOutput;
}